import { HandCoins } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import type { ShiftItemChange, WaiterCollection } from '@/services/admin';
import { formatRub } from '@/utils/format';

// Части отчёта смены, которые появились с действиями официанта на телефоне:
// причина снятия позиции и сводка денег, принятых официантами.

/** Кто снял позицию: официант с телефона или кассир смены. */
export function RemovalActor({ change }: { change: ShiftItemChange }) {
  if (change.actorName) {
    return (
      <>
        {change.actorName}
        <div className="text-slate-400 text-[10px]">телефон официанта</div>
      </>
    );
  }
  return (
    <>
      {change.cashierName ?? '—'}
      {change.deviceName ? <div className="text-slate-400 text-[10px]">{change.deviceName}</div> : null}
    </>
  );
}

/** Причина снятия, на чей счёт списание и комментарий. Снятия с кассы их не несут. */
export function RemovalReason({ change }: { change: ShiftItemChange }) {
  if (!change.reason) return null;
  const writeOff = change.reason === 'WRITE_OFF';
  return (
    <div className="mt-0.5 flex flex-wrap items-center gap-1">
      <Badge tone={writeOff ? 'warning' : 'slate'}>{writeOff ? 'со списанием' : 'без списания'}</Badge>
      {writeOff && change.writeOffPayer === 'WAITER' ? (
        <span className="text-rose-700">
          за счёт официанта{change.chargedWaiterName ? ` ${change.chargedWaiterName}` : ''}
        </span>
      ) : null}
      {writeOff && change.writeOffPayer !== 'WAITER' ? <span className="text-slate-500">за счёт заведения</span> : null}
      {change.comment ? <span className="text-slate-600">«{change.comment}»</span> : null}
    </div>
  );
}

/** «Приняли официанты»: оплаты с телефона. Наличные по ним у официанта, а не в ящике кассы. */
export function WaiterCollectionsCard({ rows }: { rows: WaiterCollection[] | undefined }) {
  if (!rows || rows.length === 0) return null;
  const cash = rows.reduce((sum, r) => sum + r.cash, 0);
  return (
    <div className="card p-4 border border-slate-100">
      <div className="flex items-center gap-2 mb-3">
        <HandCoins size={16} className="text-emerald-600" />
        <span className="font-semibold">Приняли официанты</span>
        <span className="text-xs text-slate-400">оплаты с телефона</span>
        {cash > 0 ? (
          <span className="ml-auto text-sm font-semibold text-emerald-700">наличными {formatRub(cash)}</span>
        ) : null}
      </div>
      <div className="overflow-x-auto rounded-md border border-slate-100">
        <table className="w-full text-xs tabular-nums">
          <thead>
            <tr className="bg-slate-50 text-slate-500 uppercase text-[10px]">
              <th className="text-left px-2 py-2">Официант</th>
              <th className="text-right px-2 py-2 w-20">Оплат</th>
              <th className="text-right px-2 py-2 w-28">Наличными</th>
              <th className="text-right px-2 py-2 w-28">Безнал</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.waiterId ?? r.name}>
                <td className="px-2 py-1.5 font-medium">{r.name}</td>
                <td className="px-2 py-1.5 text-right text-slate-500">{r.count}</td>
                <td className="px-2 py-1.5 text-right font-semibold text-slate-900">{formatRub(r.cash)}</td>
                <td className="px-2 py-1.5 text-right text-slate-600">{formatRub(r.cashless)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-2 text-[11px] text-slate-400">
        Деньги в выручке и смене кассы. Наличные лежат у официанта — он сдаёт их кассиру до закрытия смены.
      </div>
    </div>
  );
}
